import {
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from "react";
import {
  Card,
  Typography,
  Box,
  CircularProgress,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSelector } from "react-redux";
import { PlannerContext } from "../../context/planner/PlannerContext";
import TimeMarker from "./TimeMarker";
import TimeBlock from "./blocks/TimeBlock";
import ActivityBlock from "./blocks/ActivityBlock";
import TaskBlock from "./blocks/TaskBlock";
import LoadingScreen from "../loading/LoadingScreen";
import DateNavigator from "./DateNavigator";
import HelpLink from "../help/HelpLink";
import useSubscribe from "../../hooks/subscription/useSubscribe";

// Interval for updating "past" time markers (5 minutes in milliseconds)
const TIME_UPDATE_INTERVAL = 5 * 60 * 1000;

const DayView = () => {
  const {
    setSelectedTool,
    day,
    sortedBlocks,
    selectBlock,
    selectedBlockId,
    currentTemplate,
    timeToMinutes,
    isLoading,
    isNavigating,
    getCurrentTime,
    mockTime,
    currentDate,
    openToolPanel,
  } = useContext(PlannerContext);
  const { startTime, endTime } = day;
  const scrollContainerRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const lastTemplateRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { subscription } = useSelector((state) => state.user);

  // Check if user is on the free plan
  const isFreeUser = !subscription || subscription.plan === "free";

  const { subscribe, subscribing } = useSubscribe();

  // State to force re-render every 5 minutes for updating "past" markers
  const [timeRefreshTick, setTimeRefreshTick] = useState(0);

  // Set up interval to update "past" time markers every 5 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeRefreshTick((prev) => prev + 1);
    }, TIME_UPDATE_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  // Handle block click - select block and open tool panel on mobile
  const handleBlockClick = useCallback(
    (blockId) => {
      selectBlock(blockId);
      if (isMobile) {
        openToolPanel("editBlock");
      }
    },
    [selectBlock, isMobile, openToolPanel],
  );

  // Check if we're viewing a future day
  const isViewingFutureDay = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const viewDate = new Date(currentDate + "T00:00:00");
    viewDate.setHours(0, 0, 0, 0);
    return viewDate.getTime() > today.getTime();
  }, [currentDate]);

  // Helper function to get current time in minutes (uses mock time if set)
  // Wrapped in useCallback with mockTime dependency to trigger re-renders when mock time changes
  const getCurrentTimeInMinutes = useCallback(() => {
    const timeStr = getCurrentTime();
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  }, [getCurrentTime, mockTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoized current time in minutes - recalculates when mockTime changes or every 5 minutes
  // For future days, return 0 so nothing is marked as past
  const currentTimeMinutes = useMemo(() => {
    if (isViewingFutureDay) {
      return 0; // Nothing is in the past for a future day
    }
    return getCurrentTimeInMinutes();
  }, [getCurrentTimeInMinutes, isViewingFutureDay, timeRefreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to find the block that contains current time or the nearest upcoming block
  const findCurrentTimeBlock = useCallback(() => {
    if (!sortedBlocks || sortedBlocks.length === 0) return null;

    const currentMinutes = currentTimeMinutes;
    const dayStartMinutes = timeToMinutes(startTime);
    const dayEndMinutes = timeToMinutes(endTime);

    // If current time is before day start, return first block
    if (currentMinutes < dayStartMinutes) {
      return sortedBlocks[0];
    }

    // If current time is after day end, return last block
    if (currentMinutes > dayEndMinutes) {
      return sortedBlocks[sortedBlocks.length - 1];
    }

    // Find block that contains current time
    const currentBlock = sortedBlocks.find((block) => {
      const blockStart = timeToMinutes(block.startTime);
      const blockEnd = timeToMinutes(block.endTime);
      return currentMinutes >= blockStart && currentMinutes < blockEnd;
    });

    if (currentBlock) return currentBlock;

    // If no exact match, find the next upcoming block or the most recent past block
    let closestBlock = null;
    let closestDistance = Infinity;

    for (const block of sortedBlocks) {
      const blockStart = timeToMinutes(block.startTime);
      const distance = Math.abs(blockStart - currentMinutes);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestBlock = block;
      }
    }

    return closestBlock;
  }, [sortedBlocks, currentTimeMinutes, timeToMinutes, startTime, endTime]);

  // Auto-scroll to current time block or time marker when template loads
  useEffect(() => {
    if (!currentTemplate) {
      // Reset scroll tracking when template is cleared
      hasScrolledRef.current = false;
      lastTemplateRef.current = null;
      return;
    }

    // Only scroll on initial template load or when template changes
    if (hasScrolledRef.current && lastTemplateRef.current === currentTemplate) {
      return;
    }

    const scrollToCurrentTime = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        console.log("Scroll container ref not available");
        return;
      }

      let targetElement = null;

      if (sortedBlocks.length > 0) {
        // Find current or nearest block
        const currentBlock = findCurrentTimeBlock();
        if (!currentBlock) {
          console.log("No block found to scroll to");
          return;
        }

        // Find the element with the block's data attribute
        targetElement = container.querySelector(
          `[data-block-id="${currentBlock.id}"]`,
        );

        if (!targetElement) {
          console.log(
            "Block element not found in DOM for ID:",
            currentBlock.id,
          );
          return;
        }
      } else {
        // No blocks - find the first non-past time slot
        const dayStart = timeToMinutes(startTime);
        const dayEnd = timeToMinutes(endTime);
        const interval = day.timeInterval;
        const currentMinutes = currentTimeMinutes;

        // Find the first slot that is not in the past
        let targetSlotMinutes = null;
        for (
          let slotStart = dayStart;
          slotStart < dayEnd;
          slotStart += interval
        ) {
          const slotEnd = Math.min(slotStart + interval, dayEnd);
          if (slotEnd > currentMinutes) {
            targetSlotMinutes = slotStart;
            break;
          }
        }

        // If all slots are in the past, scroll to the last slot
        if (targetSlotMinutes === null) {
          targetSlotMinutes = dayEnd - interval;
        }

        // Find the element with the time slot's data attribute
        targetElement = container.querySelector(
          `[data-time-slot="${targetSlotMinutes}"]`,
        );

        if (!targetElement) {
          console.log(
            "Time slot element not found in DOM for minutes:",
            targetSlotMinutes,
          );
          return;
        }
      }

      // Calculate the element's position relative to the container using getBoundingClientRect
      const containerRect = container.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();

      // Current scroll position + element's position relative to container viewport
      const elementTopRelativeToContainer = targetRect.top - containerRect.top;
      const currentScrollTop = container.scrollTop;

      // The absolute position of the element within the scrollable content
      const absoluteElementTop =
        currentScrollTop + elementTopRelativeToContainer;

      // Calculate where to scroll to center the element
      const containerHeight = container.clientHeight;
      const elementHeight = targetElement.offsetHeight;
      const scrollPosition =
        absoluteElementTop - containerHeight / 2 + elementHeight / 2;

      container.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior: "smooth",
      });

      // Mark that we've scrolled for this template
      hasScrolledRef.current = true;
      lastTemplateRef.current = currentTemplate;
    };

    // Delay to ensure DOM is fully rendered
    const timer = setTimeout(scrollToCurrentTime, 500);
    return () => clearTimeout(timer);
  }, [
    currentTemplate,
    sortedBlocks,
    findCurrentTimeBlock,
    timeToMinutes,
    startTime,
    endTime,
    day.timeInterval,
    currentTimeMinutes,
  ]);

  // Show loading screen only during initial page load
  if (isLoading) {
    return <LoadingScreen />;
  }

  // If no template loaded (and not navigating), show template selection prompt or paywall
  if (!currentTemplate && !isNavigating) {
    // Show paywall for free users viewing future days
    if (isViewingFutureDay && isFreeUser) {
      return (
        <Card className="day-view day-view--empty">
          <DateNavigator />
          <Box className="day-view_empty-state">
            <LockOutlinedIcon
              sx={{ fontSize: 64, color: "primary.main", mb: 2 }}
            />
            <Typography variant="h3" sx={{ mb: 1 }}>
              Plan Ahead
            </Typography>
            <Typography
              variant="p2"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 360, textAlign: "center" }}
            >
              Planning future days is a premium feature. Subscribe to Mindstack
              to plan tomorrow and beyond.
            </Typography>
            <Button
              variant="contained"
              className="day-view_subscribe-button"
              onClick={subscribe}
              disabled={subscribing}
            >
              <span
                style={{
                  color: "#FFFFFF",
                  textTransform: "none",
                }}
              >
                {subscribing ? "Subscribing…" : "Subscribe"}
              </span>
            </Button>
          </Box>
        </Card>
      );
    }

    return (
      <Card className="day-view day-view--empty">
        <DateNavigator />
        <Box className="day-view_empty-state">
          <CalendarTodayIcon
            sx={{ fontSize: 64, color: "primary.main", mb: 2 }}
          />
          <Typography variant="h3" sx={{ mb: 1 }}>
            Plan Your Day
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Select a template to get started
          </Typography>
          {isMobile && (
            <Box
              component="button"
              onClick={() => openToolPanel("templateSelect")}
              sx={{
                backgroundColor: "primary.main",
                color: "#ffffff",
                border: "none",
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              Choose Template
            </Box>
          )}
        </Box>
      </Card>
    );
  }

  // When navigating to a new day that's not yet loaded, show the empty card with loading overlay
  if (!currentTemplate && isNavigating) {
    return (
      <Card className="day-view day-view--empty">
        <Box
          className="day-view_loading-overlay"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 10,
            borderRadius: "inherit",
          }}
        >
          <CircularProgress size={48} />
        </Box>
        <DateNavigator />
        <Box className="day-view_empty-state">
          <CalendarTodayIcon
            sx={{ fontSize: 64, color: "primary.main", mb: 2 }}
          />
          <Typography variant="h3" sx={{ mb: 1 }}>
            Plan Your Day
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select a template to get started
          </Typography>
        </Box>
      </Card>
    );
  }

  // Get the block that occupies a given time slot (if any)
  const getBlockAtTime = (slotStart, slotEnd) => {
    return sortedBlocks.find((block) => {
      const blockStart = timeToMinutes(block.startTime);
      const blockEnd = timeToMinutes(block.endTime);
      // Block occupies this slot if it overlaps with it
      return blockStart < slotEnd && blockEnd > slotStart;
    });
  };

  // Check if a time (in minutes) is in the past
  const isTimeInPast = (timeMinutes) => {
    return timeMinutes < currentTimeMinutes;
  };

  // Check if a block is in the past (ends before current time)
  const isBlockInPast = (block) => {
    const blockEndMinutes = timeToMinutes(block.endTime);
    return isTimeInPast(blockEndMinutes);
  };

  // Render the day as time interval slots
  const renderTimeSlots = () => {
    const elements = [];
    const dayStart = timeToMinutes(startTime);
    const dayEnd = timeToMinutes(endTime);
    const interval = day.timeInterval;
    const currentMinutes = currentTimeMinutes;

    // Whether the current time falls within the day's range
    const isCurrentTimeInDay =
      !isViewingFutureDay &&
      currentMinutes > dayStart &&
      currentMinutes < dayEnd;

    // Track which blocks we've already rendered
    const renderedBlockIds = new Set();

    for (let slotStart = dayStart; slotStart < dayEnd; slotStart += interval) {
      const slotEnd = Math.min(slotStart + interval, dayEnd);
      const slotTime = `${String(Math.floor(slotStart / 60)).padStart(2, "0")}:${String(slotStart % 60).padStart(2, "0")}`;
      const isSlotPast = slotEnd < currentMinutes;

      // Check if there's a block at this time slot
      const block = getBlockAtTime(slotStart, slotEnd);

      if (block && !renderedBlockIds.has(block.id)) {
        // This is the start of a block - render the time marker and block
        renderedBlockIds.add(block.id);
        const isPast = isBlockInPast(block);

        elements.push(
          <TimeMarker
            key={`time-${slotStart}`}
            time={block.startTime}
            isBlockStart={true}
            isPast={isPast}
          />,
        );

        const isSelected = selectedBlockId === block.id;
        let blockElement;

        if (block.type === "time") {
          blockElement = (
            <TimeBlock
              key={`block-${block.id}`}
              block={block}
              isSelected={isSelected}
              onClick={() => handleBlockClick(block.id)}
              isPast={isPast}
            />
          );
        } else if (block.type === "activity") {
          blockElement = (
            <ActivityBlock
              key={`block-${block.id}`}
              block={block}
              isSelected={isSelected}
              onClick={() => handleBlockClick(block.id)}
              isPast={isPast}
            />
          );
        } else if (block.type === "task") {
          blockElement = (
            <TaskBlock
              key={`block-${block.id}`}
              block={block}
              isSelected={isSelected}
              onClick={() => handleBlockClick(block.id)}
              isPast={isPast}
            />
          );
        }

        // Wrap block with data attribute for scroll targeting
        elements.push(
          <div key={`wrapper-${block.id}`} data-block-id={block.id}>
            {blockElement}
          </div>,
        );
      } else if (block && renderedBlockIds.has(block.id)) {
        // This slot is part of an already-rendered block, skip it
        continue;
      } else {
        // Empty slot - show time marker with add button
        elements.push(
          <div key={`slot-wrapper-${slotStart}`} data-time-slot={slotStart}>
            <TimeMarker
              key={`slot-${slotStart}`}
              time={slotTime}
              isEmptySlot={true}
              slotDuration={interval}
              isPast={isSlotPast}
            />
          </div>,
        );
      }
    }

    // Final end time marker
    const isEndPast = timeToMinutes(endTime) < currentMinutes;
    elements.push(
      <TimeMarker
        key="day-end"
        time={endTime}
        isEnd={true}
        isPast={isEndPast}
      />,
    );

    return elements;
  };

  return (
    <Card className="day-view">
      {isNavigating && (
        <Box
          className="day-view_loading-overlay"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 10,
            borderRadius: "inherit",
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <TuneIcon
        className="day-view_settings-icon"
        onClick={() => {
          setSelectedTool("dayConfig");
          if (isMobile) {
            openToolPanel("dayConfig");
          }
        }}
      />
      <HelpLink
        to="/about/features/day-planner"
        size="large"
        sx={{
          position: "absolute",
          top: 10,
          right: 48,
          zIndex: 2,
        }}
      />
      <DateNavigator />

      <div
        className="day-view_block-container scroller"
        ref={scrollContainerRef}
      >
        {renderTimeSlots()}
      </div>
    </Card>
  );
};

export default DayView;
